/** 体验金 / 新手模拟盘 */

/** 样式预览：true 时跳过接口直接弹窗（验收用） */
export const TRIAL_FUND_STYLE_PREVIEW = false;

/** 盘口展示价（¢）：Yes/No 同价 50¢ */
export const TRIAL_FUND_YES_CENTS = 50;
export const TRIAL_FUND_NO_CENTS = 50;

/**
 * 固定份额：本金 50¢ × 3 份 = $1.5。
 * 猜对展示与领取金额见 TRIAL_FUND_WIN_PAYOUT_USD，不再等于份额数。
 */
export const TRIAL_FUND_SHARES = 3;

/** 模拟盘投注（买卖固定名义本金，不走真实下单） */
export const TRIAL_FUND_STAKE_USD =
  Math.round(((TRIAL_FUND_YES_CENTS / 100) * TRIAL_FUND_SHARES) * 100) / 100;

/**
 * 福利盘 H5 sticky 展示档位（仅展示；可点档位见 ENABLED）
 * 独立于分钟盘全局 `ds-quick-buy-amounts`，禁止共用 localStorage。
 */
export const TRIAL_FUND_QUICK_BUY_AMOUNTS = [TRIAL_FUND_STAKE_USD, 25, 100];

/** 福利盘快捷下单仅开放本金档（与 TRIAL_FUND_STAKE_USD 一致） */
export const TRIAL_FUND_QUICK_BUY_ENABLED_AMOUNTS = [TRIAL_FUND_STAKE_USD];

/**
 * 猜对后展示的「赢取」金额，也是未登录弹窗上的 $ 金额。
 * 实际入账见 TRIAL_FUND_CLAIM_AMOUNT。
 */
export const TRIAL_FUND_WIN_PAYOUT_USD = 3;

/** 领取请求金额（≤ 服务端上限）——真实入账体验金 */
export const TRIAL_FUND_CLAIM_AMOUNT = TRIAL_FUND_WIN_PAYOUT_USD;

/**
 * 提现福利金：成交额门槛（USD）。
 * 接口 `tradeVolumeThreshold` 优先；缺省/非法时用本常量兜底。
 */
export const TRIAL_FUND_TRADE_VOLUME_THRESHOLD_USD = 200;

/** 模拟盘正确选项 */
export const TRIAL_FUND_CORRECT_SIDE = 'yes';

/** 氛围倒计时 / 开奖窗口（秒）：按服务器时间整点对齐，如 0–3、3–6 */
export const TRIAL_FUND_ROUND_SEC = 180;

/** @deprecated 与 ROUND_SEC 同值，保留别名避免旧引用断裂 */
export const TRIAL_FUND_REVEAL_SEC = TRIAL_FUND_ROUND_SEC;

/**
 * 按服务器时间对齐的当前 3 分钟窗口（epoch 整除 ROUND_SEC）
 * @param {number} [nowMs]
 * @returns {{ startMs: number, endMs: number }}
 */
export function resolveTrialFundRoundWindow(nowMs = Date.now()) {
  const interval = TRIAL_FUND_ROUND_SEC * 1000;
  const now = Number(nowMs);
  const t = Number.isFinite(now) && now > 0 ? now : Date.now();
  const startMs = Math.floor(t / interval) * interval;
  return { startMs, endMs: startMs + interval };
}

/**
 * 结算窗口展示：同日时 end 仅时分（如「8 月 6 日下午 4:45 至 4:48」）
 * @param {number} startMs
 * @param {number} endMs
 * @param {string} [locale]
 * @returns {{ start: string, end: string }}
 */
export function formatTrialFundWindowBounds(startMs, endMs, locale) {
  const startDate = new Date(startMs);
  const endDate = new Date(endMs);
  const sameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  const loc = String(locale || '').toLowerCase();
  if (loc.startsWith('zh')) {
    return {
      start: formatZhTrialFundBound(startDate, { timeOnly: false }),
      end: formatZhTrialFundBound(endDate, { timeOnly: sameDay }),
    };
  }

  const startOpts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  const endOpts = sameDay ? { hour: 'numeric', minute: '2-digit' } : startOpts;

  try {
    return {
      start: new Intl.DateTimeFormat(locale || undefined, startOpts).format(startDate),
      end: new Intl.DateTimeFormat(locale || undefined, endOpts).format(endDate),
    };
  } catch {
    return {
      start: startDate.toLocaleString(),
      end: endDate.toLocaleString(),
    };
  }
}

/**
 * 中文窗口时间：`8 月 6 日下午 4:45` / 同日仅 `4:48`
 * @param {Date} date
 * @param {{ timeOnly?: boolean }} [opts]
 */
function formatZhTrialFundBound(date, { timeOnly = false } = {}) {
  const h = date.getHours();
  const min = String(date.getMinutes()).padStart(2, '0');
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const time = `${h12}:${min}`;
  if (timeOnly) return time;
  const period = h < 12 ? '上午' : '下午';
  return `${date.getMonth() + 1} 月 ${date.getDate()} 日${period} ${time}`;
}

/**
 * i18n 插值：minutes / start / end / reward
 * @param {number} [nowMs]
 * @param {string} [locale]
 * @param {{ startMs?: number, endMs?: number }} [windowOverride] 详情页锁定轮次时可传入
 */
export function getTrialFundWindowSubtitleParams(nowMs = Date.now(), locale, windowOverride) {
  const resolved =
    windowOverride &&
    Number.isFinite(windowOverride.startMs) &&
    Number.isFinite(windowOverride.endMs)
      ? { startMs: windowOverride.startMs, endMs: windowOverride.endMs }
      : resolveTrialFundRoundWindow(nowMs);
  const { start, end } = formatTrialFundWindowBounds(resolved.startMs, resolved.endMs, locale);
  return {
    minutes: Math.round(TRIAL_FUND_ROUND_SEC / 60),
    start,
    end,
    reward: TRIAL_FUND_WIN_PAYOUT_USD,
  };
}

/** 规则说明 / 领取文案插值：reward、volume */
export function getTrialFundRuleCopyParams() {
  return {
    reward: TRIAL_FUND_WIN_PAYOUT_USD,
    volume: TRIAL_FUND_TRADE_VOLUME_THRESHOLD_USD,
  };
}

/**
 * 固定本金/份额换算（默认用定死份额）
 * @param {number} [priceCents]
 * @param {number} [stakeUsd]
 */
export function trialFundSharesForStake(
  priceCents = TRIAL_FUND_YES_CENTS,
  stakeUsd = TRIAL_FUND_STAKE_USD
) {
  if (Number(priceCents) === TRIAL_FUND_YES_CENTS && Number(stakeUsd) === TRIAL_FUND_STAKE_USD) {
    return TRIAL_FUND_SHARES;
  }
  const price = Number(priceCents) / 100;
  const stake = Number(stakeUsd);
  if (!(price > 0) || !(stake > 0)) return 0;
  return Math.round((stake / price) * 100) / 100;
}

/** 未登录弹窗：今日不再提示（已废弃，关闭改走右侧 mini） */
export const TRIAL_FUND_GUEST_SNOOZE_DATE_KEY = 'ds-trial-fund-guest-snooze-date';

/** 已登录福利弹窗：今日不再提示（已废弃，关闭改走右侧 mini） */
export const TRIAL_FUND_LOGGED_SNOOZE_DATE_KEY = 'ds-trial-fund-logged-snooze-date';

/** 未登录：本页签已关过大窗，刷新后保持右侧 mini */
export const TRIAL_FUND_GUEST_VIEW_SESSION_KEY = 'ds-trial-fund-guest-view';

/** 已登录：本页签已关过大窗，刷新后保持右侧 mini */
export const TRIAL_FUND_LOGGED_VIEW_SESSION_KEY = 'ds-trial-fund-logged-view';

/** 稍后再说后：首页插入模拟盘卡片 */
export const TRIAL_FUND_SHOW_FEED_CARD_KEY = 'ds-trial-fund-show-feed-card';

/** 已兑换成功本地标记（接口无独立 exchanged 字段时兜底） */
export const TRIAL_FUND_EXCHANGED_KEY = 'ds-trial-fund-exchanged';

/** 本机曾进入「已领取体验金」阶段（领取后小窗与个人中心卡片同显隐） */
export const TRIAL_FUND_CLAIMED_SEEN_KEY = 'ds-trial-fund-claimed-seen';

/** 登录后右侧小窗拖拽位置（session） */
export const TRIAL_FUND_LOGGED_MINI_POS_KEY = 'ds-trial-fund-logged-mini-pos';

/** 模拟盘 pending 持仓（session）：离页后全局倒计时 / 结算仍可续跑 */
export const TRIAL_FUND_SIM_PENDING_KEY = 'ds-trial-fund-sim-pending';

/** 模拟盘已赢、待充值后自动签领体验金（按 userId，localStorage） */
export const TRIAL_FUND_PENDING_CLAIM_AFTER_WIN_KEY_PREFIX =
  'ds-trial-fund-pending-claim-after-win:';

/** H5 福利盘游戏式引导：本机已完成 / 已跳过（按 userId） */
export const TRIAL_FUND_GUIDE_DONE_KEY_PREFIX = 'ds-trial-fund-guide-done:';

/** 1+1 盘口引导暂时关闭 */
export const TRIAL_FUND_GUIDE_ENABLED = false;

/** 引导步骤（主流程）：下单后即时开奖，跳过倒计时 / 回首页 */
export const TRIAL_FUND_GUIDE_STEPS = ['rules', 'chartCountdown', 'guess', 'kefu', 'bye'];

export const TRIAL_FUND_SIM_ROUTE_NAME = 'TrialFundSim';
export const TRIAL_FUND_SIM_PATH = '/trial-fund/sim';

/** 首页 Feed 插入的合成卡片 id（稳定，供 virtual list key） */
export const TRIAL_FUND_FEED_CARD_ID = 'trial-fund-sim';
export const TRIAL_FUND_FEED_CARD_KIND = 'trial-fund';

export const TRIAL_FUND_PRIMARY = '#155dfc';

/** @returns {import('@/domain/market/feed-card-model.js').FeedCardViewModel} */
export function createTrialFundFeedCardVm() {
  return {
    id: TRIAL_FUND_FEED_CARD_ID,
    kind: TRIAL_FUND_FEED_CARD_KIND,
    title: '',
    imageUrl: '',
    variant: 'binary',
  };
}

/**
 * 静态资源根路径：本地开发走 deepsage.app /resource，避免 public 旧切图。
 * 部署环境走同源。
 * @returns {string}
 */
export function resolveTrialFundResourceBase() {
  const remoteBase = 'https://deepsage.app/resource/trial-fund';
  if (typeof window === 'undefined') return remoteBase;

  const { hostname, origin } = window.location;
  const isLocalHost =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
  if (isLocalHost) return remoteBase;

  return `${origin}/resource/trial-fund`;
}

/**
 * 新手引导切图根路径（/resource/pic-yd）
 * @returns {string}
 */
export function resolveTrialFundGuideResourceBase() {
  const remoteBase = 'https://deepsage.app/resource/pic-yd';
  if (typeof window === 'undefined') return remoteBase;

  const { hostname, origin } = window.location;
  const isLocalHost =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
  if (isLocalHost) return '/resource/pic-yd';

  return `${origin}/resource/pic-yd`;
}

export function getTrialFundAssets() {
  const base = resolveTrialFundResourceBase();
  const guideBase = resolveTrialFundGuideResourceBase();
  return {
    heroChest: `${base}/hero-chest.png`,
    /** 登录引导弹窗宝箱：同源 public，避免 localhost 仍拉 CDN 旧图 */
    loginChest: '/resource/trial-fund/login-chest.png',
    /** 客服球：同源 public，避免 localhost 仍拉 CDN 旧猫头鹰图 */
    kefu: '/resource/trial-fund/kefu.png',
    /** 右侧悬浮礼物箱：同源 public，避免 localhost 仍拉 CDN 旧 16USD 图 */
    heroFc: '/resource/trial-fund/hero-fc.png',
    iconGift: `${base}/icon-gift.svg`,
    /** 登录福利弹窗第 2 行：蓝底白标，与 gift/wallet 同口径；同源 public 避免 CDN 缺图 */
    iconDeposit: '/resource/trial-fund/icon-deposit.svg',
    iconWallet: `${base}/icon-wallet.svg`,
    winGlow: `${base}/win-glow.svg`,
    loseGlow: `${base}/lose-glow.svg`,
    withdrawHeader: `${base}/withdraw-header.svg`,
    condDeposit: `${base}/cond-deposit.svg`,
    condVolume: `${base}/cond-volume.svg`,
    condMarkets: `${base}/cond-markets.svg`,
    condDays: `${base}/cond-days.svg`,
    condRisk: `${base}/cond-risk.svg`,
    condCheck: `${base}/cond-check.svg`,
    guideClock: `${guideBase}/guide-clock.png`,
    guideGift: `${guideBase}/guide-gift.png`,
    guideArrow: `${guideBase}/guide-arrow.png`,
    guideHeadset: `${guideBase}/guide-headset.png`,
    guideHand: `${guideBase}/guide-hand.png`,
    guideMoneybag: `${guideBase}/guide-moneybag.png`,
    guideBannerBg: `${guideBase}/guide-banner-bg.png`,
    guideRibbon: `${guideBase}/guide-ribbon.png`,
  };
}

let trialFundAssetsPreloadStarted = false;

/** 预加载体验金弹窗 / 兑换弹窗用到的静态图（幂等） */
export function preloadTrialFundAssets() {
  if (typeof window === 'undefined' || trialFundAssetsPreloadStarted) return;
  trialFundAssetsPreloadStarted = true;

  const assets = getTrialFundAssets();
  const urls = Object.values(assets).filter(Boolean);

  // hero 优先：link preload + Image 双保险
  const hero = assets.heroChest;
  if (hero && !document.querySelector(`link[rel="preload"][href="${hero}"]`)) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = hero;
    document.head.appendChild(link);
  }

  urls.forEach((src) => {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  });
}
